import type { Audience } from '../core/store'
import type { PaletteName } from './palettes'
import type { StageController, StageFrame, StageOptions, WorkerInMessage, WorkerOutMessage } from './types'

export class StageWorkerBridge implements StageController {
  readonly floorCount: number
  private worker: Worker
  private compilePromise: { resolve: () => void; reject: (err: unknown) => void } | null = null
  private disposed = false

  // Deduplication state for frame updates
  private lastShotJson = ''
  private lastProcess = -1
  private lastPointerX = -999
  private lastPointerY = -999

  private onPointerMove: ((e: PointerEvent) => void) | null = null
  private onVisibilityChange: (() => void) | null = null

  constructor(
    canvas: HTMLCanvasElement,
    opts: StageOptions,
    onContextLost?: () => void,
  ) {
    this.floorCount = opts.lowPower ? 14 : 18

    const offscreen = canvas.transferControlToOffscreen()
    this.worker = new Worker(new URL('./stage.worker.ts', import.meta.url), {
      type: 'module',
    })

    this.worker.onmessage = (event: MessageEvent<WorkerOutMessage>) => {
      const msg = event.data
      if (!msg) return
      switch (msg.type) {
        case 'COMPILED':
          this.compilePromise?.resolve()
          this.compilePromise = null
          break
        case 'CONTEXT_LOST':
          onContextLost?.()
          break
        case 'ERROR':
          console.error('[stage-worker]', msg.error)
          break
      }
    }

    const dpr = Math.min(window.devicePixelRatio || 1, opts.lowPower ? 1.5 : 2)
    const initMsg: WorkerInMessage = {
      type: 'INIT',
      canvas: offscreen,
      opts,
      width: canvas.clientWidth || window.innerWidth,
      height: canvas.clientHeight || window.innerHeight,
      dpr,
      isDev: import.meta.env.DEV,
      timerRaf: location.search.includes('timer-raf'),
    }

    this.worker.postMessage(initMsg, [offscreen])

    // Forward pointer events through message passing to guarantee 0ms INP under high UI interaction loads
    this.onPointerMove = (e: PointerEvent) => {
      if (this.disposed) return
      const msg: WorkerInMessage = {
        type: 'POINTER_MOVE',
        clientX: e.clientX,
        clientY: e.clientY,
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
      }
      this.worker.postMessage(msg)
    }
    window.addEventListener('pointermove', this.onPointerMove, { passive: true })

    // Pause worker render loop when tab is backgrounded
    this.onVisibilityChange = () => {
      if (this.disposed) return
      const msg: WorkerInMessage = {
        type: 'VISIBILITY',
        visible: !document.hidden,
      }
      this.worker.postMessage(msg)
    }
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  }

  setPalette(name: PaletteName, duration = 0.9): void {
    if (this.disposed) return
    const msg: WorkerInMessage = { type: 'PALETTE', name, duration }
    this.worker.postMessage(msg)
  }

  setAudience(audience: Audience): void {
    if (this.disposed) return
    const msg: WorkerInMessage = { type: 'AUDIENCE', audience }
    this.worker.postMessage(msg)
  }

  setGyro(roll: number, pitch: number): void {
    if (this.disposed) return
    const msg: WorkerInMessage = { type: 'GYRO_MOVE', roll, pitch }
    this.worker.postMessage(msg)
  }

  pulse(): void {
    if (this.disposed) return
    const msg: WorkerInMessage = { type: 'PULSE' }
    this.worker.postMessage(msg)
  }

  glitchSpike(): void {
    if (this.disposed) return
    const msg: WorkerInMessage = { type: 'GLITCH' }
    this.worker.postMessage(msg)
  }

  startIntro(): void {
    if (this.disposed) return
    const msg: WorkerInMessage = { type: 'START_INTRO' }
    this.worker.postMessage(msg)
  }

  resize(width: number, height: number, dpr?: number): void {
    if (this.disposed) return
    const clientDpr = dpr ?? window.devicePixelRatio ?? 1
    const msg: WorkerInMessage = { type: 'RESIZE', width, height, dpr: clientDpr }
    this.worker.postMessage(msg)
  }

  renderNow(): void {
    if (this.disposed) return
    const msg: WorkerInMessage = { type: 'RENDER_NOW' }
    this.worker.postMessage(msg)
  }

  render(_time: number, _dt: number, frame: StageFrame): void {
    if (this.disposed) return

    // Quick check to avoid redundant postMessage calls when stationary
    const shot = frame.shot
    const shotKey = `${shot.az.toFixed(3)},${shot.el.toFixed(3)},${shot.dist.toFixed(2)},${shot.fy.toFixed(2)}`
    const changed =
      shotKey !== this.lastShotJson ||
      Math.abs(frame.process - this.lastProcess) > 0.0005 ||
      Math.abs(frame.pointerX - this.lastPointerX) > 0.001 ||
      Math.abs(frame.pointerY - this.lastPointerY) > 0.001

    if (changed) {
      this.lastShotJson = shotKey
      this.lastProcess = frame.process
      this.lastPointerX = frame.pointerX
      this.lastPointerY = frame.pointerY

      const msg: WorkerInMessage = {
        type: 'UPDATE_FRAME',
        frame: {
          shot: frame.shot,
          process: frame.process,
          pointerX: frame.pointerX,
          pointerY: frame.pointerY,
        },
      }
      this.worker.postMessage(msg)
    }
  }

  compileShaders(): Promise<void> {
    if (this.disposed) return Promise.resolve()
    return new Promise<void>((resolve, reject) => {
      this.compilePromise = { resolve, reject }
      const msg: WorkerInMessage = { type: 'COMPILE' }
      this.worker.postMessage(msg)

      // Guard fallback: resolve after 3500ms so preloader never hangs
      setTimeout(() => {
        if (this.compilePromise) {
          this.compilePromise.resolve()
          this.compilePromise = null
        }
      }, 3500)
    })
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true

    if (this.onPointerMove) {
      window.removeEventListener('pointermove', this.onPointerMove)
      this.onPointerMove = null
    }
    if (this.onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
      this.onVisibilityChange = null
    }

    const msg: WorkerInMessage = { type: 'DISPOSE' }
    this.worker.postMessage(msg)
    this.worker.terminate()
  }
}
